import { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { getModuleEntryByErpModule, getModuleEntryBySlug } from '@/config/moduleEntries';
import { ORG_SIGN_IN_PATH } from '@/constants/authRoutes';
import { LoginForm } from '@/pages/auth/LoginForm';
import { AppLauncher } from '@/components/layout/AppLauncher';
import { clearPlatformOnboardingRequired } from '@/modules/onboarding/onboardingState';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { setWorkspaceModule, switchWorkspace } from '@/store/authSlice';
import { getLauncherModules, isOrgOwner } from '@/utils/moduleAccess';
import { markWorkspaceModuleUsed, resolveModuleHomePath } from '@/utils/moduleScope';
import { notify } from '@/core/toast/notify';
import { usePermissions } from '@/hooks/usePermissions';
import type { ErpModule } from '@/api/platform.api';

interface ModuleLoginPageProps {
  slug: string;
}

export function ModuleLoginPage({ slug }: ModuleLoginPageProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { has } = usePermissions();
  const moduleEntry = getModuleEntryBySlug(slug);
  const {
    isAuthenticated,
    user,
    entryModule,
    workspaceModule,
    enabledModules,
    accessibleModules,
    loading,
  } = useAppSelector((s) => s.auth);

  const activeModule = (entryModule ?? workspaceModule) as ErpModule | null;
  const permissions = user?.permissions ?? [];
  const roleCode = user?.roleCode;

  const launcherApps = getLauncherModules(
    enabledModules,
    permissions,
    accessibleModules,
    roleCode,
    has,
  );

  const canAccessTarget =
    moduleEntry != null &&
    launcherApps.some((entry) => entry.erpModule === moduleEntry.erpModule);

  const signedIntoOtherApp =
    Boolean(moduleEntry) &&
    isAuthenticated &&
    Boolean(user) &&
    !user?.masterAdmin &&
    Boolean(activeModule) &&
    activeModule !== moduleEntry?.erpModule &&
    canAccessTarget;

  const targetHomePath =
    moduleEntry != null
      ? resolveModuleHomePath(
          moduleEntry.erpModule,
          permissions,
          accessibleModules,
          roleCode,
          moduleEntry.homePath,
        )
      : ORG_SIGN_IN_PATH;

  const finishModuleLogin = (result?: {
    permissions?: string[];
    roleCode?: string | null;
  }) => {
    if (!moduleEntry) return;
    clearPlatformOnboardingRequired();
    dispatch(setWorkspaceModule(moduleEntry.erpModule));
    markWorkspaceModuleUsed(moduleEntry.erpModule);
    navigate(targetHomePath, { replace: true });
    if (isOrgOwner(result?.permissions ?? permissions, result?.roleCode ?? roleCode)) {
      void dispatch(switchWorkspace(moduleEntry.erpModule));
    }
  };

  useEffect(() => {
    if (!moduleEntry || !isAuthenticated || !user || user.masterAdmin) return;
    if (signedIntoOtherApp) return;
    if (activeModule === moduleEntry.erpModule) {
      navigate(targetHomePath, { replace: true });
    }
  }, [
    moduleEntry,
    isAuthenticated,
    user,
    activeModule,
    signedIntoOtherApp,
    targetHomePath,
    navigate,
  ]);

  if (!moduleEntry) {
    return <Navigate to={ORG_SIGN_IN_PATH} replace />;
  }

  const openTargetApp = async () => {
    try {
      await dispatch(switchWorkspace(moduleEntry.erpModule)).unwrap();
      clearPlatformOnboardingRequired();
      markWorkspaceModuleUsed(moduleEntry.erpModule);
      navigate(targetHomePath, { replace: true });
      notify.success(`Opened ${moduleEntry.label}`);
    } catch {
      notify.error(`Could not open ${moduleEntry.label}`);
    }
  };

  if (signedIntoOtherApp) {
    const currentLabel = getModuleEntryByErpModule(activeModule!)?.label ?? activeModule;
    return (
      <div className="space-y-5">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Signed in as <span className="font-medium text-slate-900 dark:text-slate-100">{user?.email}</span>
          </p>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            You are currently in <span className="font-medium text-slate-900 dark:text-slate-100">{currentLabel}</span>.
            Open <span className="font-medium text-slate-900 dark:text-slate-100">{moduleEntry.label}</span> instead?
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
              disabled={loading}
              onClick={() => void openTargetApp()}
            >
              Open {moduleEntry.label}
            </button>
            {activeModule && launcherApps.length >= 2 ? (
              <AppLauncher activeModule={activeModule} />
            ) : null}
          </div>
        </div>
        <LoginForm
          moduleEntry={moduleEntry}
          showRegisterLink
          onLoginSuccess={(result) => finishModuleLogin(result)}
        />
      </div>
    );
  }

  if (isAuthenticated && loading) {
    return (
      <div className="py-12 text-center text-sm text-slate-500 dark:text-slate-400">
        Opening {moduleEntry.label}…
      </div>
    );
  }

  return (
    <LoginForm
      moduleEntry={moduleEntry}
      showRegisterLink
      onLoginSuccess={(result) => finishModuleLogin(result)}
    />
  );
}
